import { parseThresholdValue } from '@/features/projects/metricThresholdDrafts';

export type ThresholdDirection = 'higherBetter' | 'lowerBetter';
export type ThresholdTone = 'red' | 'yellow' | 'green';
export type FrequencyPreviewUnit = 'hour' | 'day' | 'week' | 'month';

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const toSingularUnitLabel = (label: string): string => label.replace(/s$/, '');
export const toPluralUnitLabel = (label: string): string =>
  label.endsWith('s') ? label : `${label}s`;
export const isSingularValue = (value: string): boolean => parseThresholdValue(value) === 1;

export const toFrequencyPreviewUnit = (
  unit: 'hours' | 'days' | 'weeks' | 'months',
): FrequencyPreviewUnit => {
  if (unit === 'hours') return 'hour';
  if (unit === 'days') return 'day';
  if (unit === 'weeks') return 'week';
  return 'month';
};

export const resolveThresholdTone = (
  value: number,
  thresholds: { warn: number; target: number; direction: ThresholdDirection },
): ThresholdTone => {
  if (thresholds.direction === 'higherBetter') {
    if (value >= thresholds.target) return 'green';
    if (value >= thresholds.warn) return 'yellow';
    return 'red';
  }
  if (value <= thresholds.target) return 'green';
  if (value <= thresholds.warn) return 'yellow';
  return 'red';
};

export const buildPreviewValues = (
  thresholds: { warn: number; target: number; direction: ThresholdDirection },
  bounds: { min: number; max: number; step: number },
): { red: number; yellowMin: number; yellowMax: number; green: number } => {
  const normalizedWarn = clamp(thresholds.warn, bounds.min, bounds.max);
  const normalizedTarget = clamp(thresholds.target, bounds.min, bounds.max);

  if (thresholds.direction === 'higherBetter') {
    const red = clamp(normalizedWarn - bounds.step, bounds.min, bounds.max);
    const yellowMin = normalizedWarn;
    const yellowMax = clamp(normalizedTarget - bounds.step, bounds.min, bounds.max);
    const green = normalizedTarget;
    return { red, yellowMin, yellowMax, green };
  }

  const green = normalizedTarget;
  const yellowMin = clamp(normalizedTarget + bounds.step, bounds.min, bounds.max);
  const yellowMax = normalizedWarn;
  const red = clamp(
    Math.max(normalizedWarn, normalizedTarget) + bounds.step,
    bounds.min,
    bounds.max,
  );
  return { red, yellowMin, yellowMax, green };
};
